import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Check,
  X,
  Users,
  Globe,
  FileText,
  BarChart3,
  Sparkles,
  MessageSquare,
  FolderKanban,
  Target,
  TrendingUp,
  Clock,
  Shield,
  Play,
  Instagram,
  Linkedin,
  ChevronDown,
} from 'lucide-react';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';
import i18n from '../i18n';

interface LandingProps {
  lang?: SupportedLanguage;
}

export function Landing({ lang }: LandingProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  useEffect(() => {
    if (lang && SUPPORTED_LANGUAGES.some((l) => l.code === lang)) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  const handleLanguageChange = (code: SupportedLanguage) => {
    i18n.changeLanguage(code);
    setLangMenuOpen(false);
    if (code === 'de') {
      navigate('/');
    } else {
      navigate(`/${code}`);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div
      className="min-h-screen bt-bg"
      style={{
        // Always use default purple on the public landing page,
        // regardless of any user-selected accent color.
        ['--color-primary' as string]: '#9B6BFF',
        ['--color-primary-light' as string]: '#C9A7FF',
        ['--color-primary-lighter' as string]: '#E4D3FF',
        ['--color-primary-dark' as string]: '#7C56CC',
        ['--color-primary-rgb' as string]: '155, 107, 255',
      }}
    >
      {/* Header / Navigation */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          maxHeight: '72px',
          background: isScrolled ? 'var(--bt-card)' : 'rgba(255,255,255,0.10)',
          backdropFilter: 'blur(var(--bt-blur)) saturate(160%)',
          WebkitBackdropFilter: 'blur(var(--bt-blur)) saturate(160%)',
          borderBottom: isScrolled ? '1px solid var(--bt-glass-stroke)' : '1px solid transparent',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <div
                style={{
                  width: 32,
                  height: 32,
                  background: 'var(--bt-ink)',
                  borderRadius: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontStyle: 'italic',
                    color: 'var(--bt-ink-2)',
                    fontSize: 18,
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  m
                </span>
              </div>
              <span
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontStyle: 'italic',
                  fontSize: 22,
                  fontWeight: 500,
                  color: 'var(--bt-ink)',
                  letterSpacing: '-0.02em',
                }}
              >
                mamal
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('features')}
                className="text-[#6B6B6B] hover:text-[#1A1A1A] font-medium transition-colors"
              >
                {t('landing.nav.features')}
              </button>
              <button
                onClick={() => scrollToSection('zielgruppe')}
                className="text-[#6B6B6B] hover:text-[#1A1A1A] font-medium transition-colors"
              >
                {t('landing.nav.forWho')}
              </button>
              <button
                onClick={() => scrollToSection('preise')}
                className="text-[#6B6B6B] hover:text-[#1A1A1A] font-medium transition-colors"
              >
                {t('landing.nav.pricing')}
              </button>
              <button
                onClick={() => scrollToSection('hero')}
                className="text-[#6B6B6B] hover:text-[#1A1A1A] font-medium transition-colors"
              >
                {t('landing.nav.demo')}
              </button>
            </nav>

            {/* CTA Buttons + Language Switcher */}
            <div className="hidden md:flex items-center gap-4">
              {/* Language Switcher */}
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center gap-1.5 text-[#6B6B6B] hover:text-[#1A1A1A] font-medium transition-colors px-2 py-1.5 rounded-lg hover:bg-gray-50"
                >
                  <span className="text-lg">{currentLang.flag}</span>
                  <span className="text-sm">{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {langMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[160px] z-50">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          i18n.language === lang.code ? 'text-primary font-semibold' : 'text-[#1A1A1A]'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/login"
                className="text-[#6B6B6B] hover:text-[#1A1A1A] font-medium transition-colors"
              >
                {t('landing.nav.login')}
              </Link>
              <Link
                to="/register"
                className="bg-primary text-white px-6 py-2.5 rounded-xl font-medium hover:bg-primary-dark transition-all shadow-md hover:shadow-lg"
              >
                {t('landing.nav.startFree')}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-[#6B6B6B]"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`block h-0.5 w-full bg-current transition-transform ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`block h-0.5 w-full bg-current transition-opacity ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                <span className={`block h-0.5 w-full bg-current transition-transform ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="md:hidden"
            style={{
              background: 'var(--bt-card)',
              backdropFilter: 'blur(var(--bt-blur)) saturate(160%)',
              WebkitBackdropFilter: 'blur(var(--bt-blur)) saturate(160%)',
              borderTop: '1px solid var(--bt-glass-stroke)',
            }}
          >
            <div className="px-4 py-4 space-y-4">
              <button
                onClick={() => scrollToSection('features')}
                className="block w-full text-left text-[#6B6B6B] hover:text-[#1A1A1A] font-medium py-2"
              >
                {t('landing.nav.features')}
              </button>
              <button
                onClick={() => scrollToSection('zielgruppe')}
                className="block w-full text-left text-[#6B6B6B] hover:text-[#1A1A1A] font-medium py-2"
              >
                {t('landing.nav.forWho')}
              </button>
              <button
                onClick={() => scrollToSection('preise')}
                className="block w-full text-left text-[#6B6B6B] hover:text-[#1A1A1A] font-medium py-2"
              >
                {t('landing.nav.pricing')}
              </button>
              {/* Mobile Language Switcher */}
              <div className="pt-2 border-t border-gray-50">
                <p className="text-xs text-[#6B6B6B] mb-2 font-medium">Sprache / Language</p>
                <div className="flex flex-wrap gap-2">
                  {SUPPORTED_LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => handleLanguageChange(l.code)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm transition-colors ${
                        i18n.language === l.code
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'bg-gray-50 text-[#6B6B6B] hover:bg-gray-100'
                      }`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-4 border-t border-gray-100 space-y-3">
                <Link
                  to="/login"
                  className="block text-center text-[#6B6B6B] font-medium py-2"
                >
                  {t('landing.nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="block text-center bg-primary text-white px-6 py-3 rounded-xl font-medium"
                >
                  {t('landing.nav.startFree')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="pt-[72px]">
        {/* Hero Section */}
        <section id="hero" className="relative overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Content */}
              <div className="space-y-8">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-tight">
                  {t('landing.hero.title1')}
                  <br />
                  <span className="text-primary">{t('landing.hero.title2')}</span>
                  <br />
                  {t('landing.hero.title3')}
                </h1>

                <p className="text-lg sm:text-xl text-[#6B6B6B] leading-relaxed max-w-xl">
                  {t('landing.hero.subtitle')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-xl font-semibold hover:bg-primary-dark transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    {t('landing.hero.startFree')}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <button
                    onClick={() => scrollToSection('features')}
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#1A1A1A] px-8 py-4 rounded-xl font-semibold border-2 border-gray-200 hover:border-primary/70 hover:bg-[#F8F6FF] transition-all"
                  >
                    <Play className="w-5 h-5" />
                    {t('landing.hero.watchDemo')}
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 pt-4">
                  <div className="flex items-center gap-2 text-[#6B6B6B]">
                    <Check className="w-5 h-5 text-primary" />
                    <span>{t('landing.hero.noCreditCard')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#6B6B6B]">
                    <Check className="w-5 h-5 text-primary" />
                    <span>{t('landing.hero.readyInMinutes')}</span>
                  </div>
                </div>
              </div>

              {/* Right - Dashboard Mockup */}
              <div className="relative">
                <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
                  {/* Mockup Header */}
                  <div className="bg-gradient-to-r from-primary to-primary/70 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-white/30" />
                      <div className="w-3 h-3 rounded-full bg-white/30" />
                      <div className="w-3 h-3 rounded-full bg-white/30" />
                    </div>
                  </div>
                  {/* Mockup Content */}
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="h-6 w-32 bg-gray-100 rounded-lg" />
                      <div className="h-8 w-24 bg-primary/10 rounded-lg" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-2">
                          <div className="h-8 w-8 bg-primary/20 rounded-lg" />
                          <div className="h-4 w-16 bg-gray-200 rounded" />
                          <div className="h-6 w-12 bg-primary rounded text-white text-xs flex items-center justify-center font-bold">
                            {i * 12}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <div className="w-10 h-10 bg-primary/20 rounded-full" />
                          <div className="flex-1 space-y-1">
                            <div className="h-3 w-24 bg-gray-200 rounded" />
                            <div className="h-2 w-32 bg-gray-100 rounded" />
                          </div>
                          <div className={`h-6 w-16 rounded-full text-xs flex items-center justify-center font-medium ${
                            i === 1 ? 'bg-green-100 text-green-700' : i === 2 ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {i === 1 ? t('landing.mockup.hero.statusNew') : i === 2 ? t('landing.mockup.hero.statusContact') : t('landing.mockup.hero.statusConversation')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-3 border border-gray-100 animate-pulse">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">{t('landing.mockup.analytics.conversionRate')}</div>
                      <div className="text-sm font-bold text-green-600">+24%</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-3 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">{t('landing.mockup.hero.newLeads')}</div>
                      <div className="text-sm font-bold text-[#1A1A1A]">{t('landing.mockup.hero.todayCount')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-16 lg:py-24" style={{ background: 'rgba(255,255,255,0.12)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
                {t('landing.problem.title')}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[
                {
                  icon: MessageSquare,
                  text: t('landing.problem.item1'),
                },
                {
                  icon: FolderKanban,
                  text: t('landing.problem.item2'),
                },
                {
                  icon: Target,
                  text: t('landing.problem.item3'),
                },
                {
                  icon: Globe,
                  text: t('landing.problem.item4'),
                },
                {
                  icon: BarChart3,
                  text: t('landing.problem.item5'),
                },
                {
                  icon: Clock,
                  text: t('landing.problem.item6'),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                >
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-red-400" />
                  </div>
                  <p className="text-[#1A1A1A] font-medium">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <div className="inline-block bg-white rounded-2xl px-8 py-6 shadow-sm border border-gray-100">
                <p className="text-lg text-[#6B6B6B]">
                  <span className="font-semibold text-[#1A1A1A]">{t('landing.problem.result')}</span>{' '}
                  {t('landing.problem.resultText')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
                {t('landing.solution.title')}
              </h2>
              <p className="text-lg text-[#6B6B6B] max-w-3xl mx-auto">
                {t('landing.solution.subtitle')}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                { icon: Users, title: t('landing.solution.item1') },
                { icon: Globe, title: t('landing.solution.item2') },
                { icon: FileText, title: t('landing.solution.item3') },
                { icon: BarChart3, title: t('landing.solution.item4') },
                { icon: Sparkles, title: t('landing.solution.item5') },
              ].map((item, index) => (
                <div
                  key={index}
                  className="text-center p-6 rounded-2xl hover:bg-[#F8F6FF] transition-colors group"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                    <item.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-[#1A1A1A]">{item.title}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-16 lg:py-24" style={{ background: 'rgba(255,255,255,0.12)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
                {t('landing.features.badge')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A]">
                {t('landing.features.title')}
              </h2>
            </div>

            {/* Feature 1 - CRM */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">{t('landing.features.crm.badge')}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">
                  {t('landing.features.crm.title')}
                </h3>
                <ul className="space-y-4">
                  {[
                    t('landing.features.crm.item1'),
                    t('landing.features.crm.item2'),
                    t('landing.features.crm.item3'),
                    t('landing.features.crm.item4'),
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[#6B6B6B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-[#1A1A1A]">{t('landing.mockup.crm.pipelineTitle')}</h4>
                    <div className="text-sm text-[#6B6B6B]">{t('landing.mockup.crm.leadsCount')}</div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-sm">
                    {[t('landing.mockup.crm.statusNew'), t('landing.mockup.crm.statusContacted'), t('landing.mockup.crm.statusConversation'), t('landing.mockup.crm.statusClose')].map((status, i) => (
                      <div key={status} className="bg-gray-50 rounded-lg py-2">
                        <div className="font-medium text-[#1A1A1A]">{status}</div>
                        <div className="text-primary font-bold">{[4, 3, 3, 2][i]}</div>
                      </div>
                    ))}
                  </div>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary/70 to-primary rounded-full flex items-center justify-center text-white font-medium text-sm">
                        {['SK', 'MH', 'LP'][i - 1]}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-[#1A1A1A] text-sm">
                          {['Sarah Klein', 'Max Hoffmann', 'Lisa Peters'][i - 1]}
                        </div>
                        <div className="text-xs text-[#6B6B6B]">
                          {['Instagram', t('landing.mockup.crm.sourceForm'), 'WhatsApp'][i - 1]}
                        </div>
                      </div>
                      <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                        i === 1 ? 'bg-blue-100 text-blue-700' : i === 2 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {[t('landing.mockup.crm.statusNewShort'), t('landing.mockup.crm.statusContactShort'), t('landing.mockup.crm.statusConvoShort')][i - 1]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Feature 2 - Website Builder */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
              <div className="order-2 lg:order-1 bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-gradient-to-r from-primary to-primary/70 px-4 py-2 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-white/40" />
                  <div className="w-2 h-2 rounded-full bg-white/40" />
                  <div className="w-2 h-2 rounded-full bg-white/40" />
                  <span className="text-white/80 text-xs ml-2">{t('landing.mockup.website.editorTitle')}</span>
                </div>
                <div className="p-4 grid grid-cols-3 gap-4">
                  <div className="col-span-1 space-y-2">
                    <div className="text-xs font-medium text-[#6B6B6B] mb-2">{t('landing.mockup.website.blocks')}</div>
                    {['Hero', t('landing.mockup.website.blockAbout'), t('landing.mockup.website.blockAdvantages'), t('landing.mockup.website.blockForm')].map((block) => (
                      <div key={block} className="bg-gray-50 rounded-lg p-2 text-xs text-[#1A1A1A] cursor-pointer hover:bg-primary/10 transition-colors">
                        {block}
                      </div>
                    ))}
                  </div>
                  <div className="col-span-2 bg-gray-50 rounded-xl p-4">
                    <div className="bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg p-4 mb-3">
                      <div className="h-3 w-24 bg-primary/30 rounded mb-2" />
                      <div className="h-2 w-32 bg-gray-200 rounded" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-full bg-gray-200 rounded" />
                      <div className="h-2 w-3/4 bg-gray-200 rounded" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                  <Globe className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">{t('landing.features.website.badge')}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">
                  {t('landing.features.website.title')}
                </h3>
                <ul className="space-y-4">
                  {[
                    t('landing.features.website.item1'),
                    t('landing.features.website.item2'),
                    t('landing.features.website.item3'),
                    t('landing.features.website.item4'),
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[#6B6B6B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Feature 3 - Forms & Tracking */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">{t('landing.features.forms.badge')}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">
                  {t('landing.features.forms.title')}
                </h3>
                <ul className="space-y-4">
                  {[
                    t('landing.features.forms.item1'),
                    t('landing.features.forms.item2'),
                    t('landing.features.forms.item3'),
                    t('landing.features.forms.item4'),
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[#6B6B6B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-[#1A1A1A]">{t('landing.mockup.analytics.trafficTitle')}</h4>
                  <span className="text-sm text-[#6B6B6B]">{t('landing.mockup.analytics.lastSevenDays')}</span>
                </div>
                <div className="space-y-4">
                  {[
                    { source: 'Instagram', visits: 124, conversion: '12%', color: 'bg-pink-500' },
                    { source: t('landing.mockup.analytics.sourceDirect'), visits: 89, conversion: '8%', color: 'bg-blue-500' },
                    { source: 'WhatsApp', visits: 45, conversion: '22%', color: 'bg-green-500' },
                  ].map((item) => (
                    <div key={item.source} className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${item.color}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-[#1A1A1A]">{item.source}</span>
                          <span className="text-sm text-[#6B6B6B]">{t('landing.mockup.analytics.visits', { count: item.visits })}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${item.color} rounded-full`}
                            style={{ width: `${(item.visits / 124) * 100}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-primary">{item.conversion}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Feature 4 - Analytics */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
              <div className="order-2 lg:order-1 bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-[#1A1A1A]">{t('landing.mockup.analytics.dashboardTitle')}</h4>
                  <span className="text-sm text-primary font-medium">{t('landing.mockup.analytics.thisWeek')}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { label: t('landing.mockup.analytics.pageViews'), value: '1.234', change: '+12%' },
                    { label: t('landing.mockup.analytics.newLeads'), value: '23', change: '+8%' },
                    { label: t('landing.mockup.analytics.conversionRate'), value: '4.2%', change: '+0.5%' },
                    { label: t('landing.mockup.analytics.formStarts'), value: '67', change: '+15%' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-gray-50 rounded-xl p-4">
                      <div className="text-sm text-[#6B6B6B] mb-1">{stat.label}</div>
                      <div className="flex items-end gap-2">
                        <span className="text-2xl font-bold text-[#1A1A1A]">{stat.value}</span>
                        <span className="text-sm text-green-600 font-medium">{stat.change}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="h-24 bg-gradient-to-t from-primary/10 to-transparent rounded-xl flex items-end px-4 pb-2">
                  {[40, 65, 45, 80, 60, 90, 75].map((h, i) => (
                    <div key={i} className="flex-1 flex justify-center">
                      <div
                        className="w-4 bg-primary rounded-t"
                        style={{ height: `${h}%` }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">{t('landing.features.analytics.badge')}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">
                  {t('landing.features.analytics.title')}
                </h3>
                <ul className="space-y-4">
                  {[
                    t('landing.features.analytics.item1'),
                    t('landing.features.analytics.item2'),
                    t('landing.features.analytics.item3'),
                    t('landing.features.analytics.item4'),
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[#6B6B6B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Feature 5 - AI */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">{t('landing.features.ai.badge')}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">
                  {t('landing.features.ai.title')}
                </h3>
                <ul className="space-y-4">
                  {[
                    t('landing.features.ai.item1'),
                    t('landing.features.ai.item2'),
                    t('landing.features.ai.item3'),
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[#6B6B6B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-xl border border-primary/20">
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-[#1A1A1A] mb-1">{t('landing.mockup.ai.suggestionTitle')}</div>
                      <p className="text-sm text-[#6B6B6B]">
                        "{t('landing.mockup.ai.suggestionText')}"
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-[#1A1A1A] mb-1">{t('landing.mockup.ai.tipTitle')}</div>
                      <p className="text-sm text-[#6B6B6B]">
                        "{t('landing.mockup.ai.tipText')}"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Target Audience Section */}
        <section id="zielgruppe" className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
                {t('landing.audience.title')}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Suitable for */}
              <div className="bg-[#F8F6FF] rounded-2xl p-8 border border-primary/20">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A1A]">{t('landing.audience.suitable')}</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    t('landing.audience.suitableItem1'),
                    t('landing.audience.suitableItem2'),
                    t('landing.audience.suitableItem3'),
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-[#1A1A1A] font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Not suitable for */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gray-400 rounded-xl flex items-center justify-center">
                    <X className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A1A]">{t('landing.audience.notSuitable')}</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    t('landing.audience.notSuitableItem1'),
                    t('landing.audience.notSuitableItem2'),
                    t('landing.audience.notSuitableItem3'),
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <span className="text-[#6B6B6B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 lg:py-24" style={{ background: 'rgba(255,255,255,0.12)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
                {t('landing.results.title')}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                { icon: Users, text: t('landing.results.item1') },
                { icon: Target, text: t('landing.results.item2') },
                { icon: TrendingUp, text: t('landing.results.item3') },
                { icon: Globe, text: t('landing.results.item4') },
                { icon: Clock, text: t('landing.results.item5') },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-primary" />
                  </div>
                  <p className="font-semibold text-[#1A1A1A]">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-br from-[#F8F6FF] to-white rounded-3xl p-8 lg:p-12 border border-primary/10">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-4">
                {t('landing.trust.title')}
              </h2>
              <p className="text-lg text-[#6B6B6B] max-w-2xl mx-auto">
                {t('landing.trust.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="preise" className="py-16 lg:py-24" style={{ background: 'rgba(255,255,255,0.12)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
                {t('landing.pricing.title')}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Free Plan */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{t('landing.pricing.free.name')}</h3>
                  <p className="text-[#6B6B6B] text-sm">{t('landing.pricing.free.tagline')}</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#1A1A1A]">0€</span>
                  <span className="text-[#6B6B6B]">{t('landing.pricing.perMonth')}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    t('landing.pricing.free.feature1'),
                    t('landing.pricing.free.feature2'),
                    t('landing.pricing.free.feature3'),
                    t('landing.pricing.free.feature4'),
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-primary" />
                      <span className="text-[#6B6B6B] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className="block w-full text-center bg-gray-100 text-[#1A1A1A] px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  {t('landing.pricing.startNow')}
                </Link>
              </div>

              {/* Pro Plan - Highlighted */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-primary relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {t('landing.pricing.popular')}
                </div>
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{t('landing.pricing.pro.name')}</h3>
                  <p className="text-[#6B6B6B] text-sm">{t('landing.pricing.pro.tagline')}</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#1A1A1A]">19€</span>
                  <span className="text-[#6B6B6B]">{t('landing.pricing.perMonth')}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    t('landing.pricing.pro.feature1'),
                    t('landing.pricing.pro.feature2'),
                    t('landing.pricing.pro.feature3'),
                    t('landing.pricing.pro.feature4'),
                    t('landing.pricing.pro.feature5'),
                    t('landing.pricing.pro.feature6'),
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-primary" />
                      <span className="text-[#6B6B6B] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className="block w-full text-center bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors shadow-md"
                >
                  {t('landing.pricing.startNow')}
                </Link>
              </div>

              {/* Advanced Plan */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{t('landing.pricing.advanced.name')}</h3>
                  <p className="text-[#6B6B6B] text-sm">{t('landing.pricing.advanced.tagline')}</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#1A1A1A]">39€</span>
                  <span className="text-[#6B6B6B]">{t('landing.pricing.perMonth')}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    t('landing.pricing.advanced.feature1'),
                    t('landing.pricing.advanced.feature2'),
                    t('landing.pricing.advanced.feature3'),
                    t('landing.pricing.advanced.feature4'),
                    t('landing.pricing.advanced.feature5'),
                    t('landing.pricing.advanced.feature6'),
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-primary" />
                      <span className="text-[#6B6B6B] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className="block w-full text-center bg-gray-100 text-[#1A1A1A] px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  {t('landing.pricing.startNow')}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 lg:py-24" style={{ background: 'var(--bt-black)' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('landing.cta.title')}
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              {t('landing.cta.subtitle')}
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-10 py-4 rounded-xl font-semibold hover:bg-primary-dark transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-lg"
            >
              {t('landing.cta.button')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 mt-6">
              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Check className="w-5 h-5 text-primary" />
                <span>{t('landing.cta.noCreditCard')}</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Check className="w-5 h-5 text-primary" />
                <span>{t('landing.cta.cancelAnytime')}</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: 'rgba(255,255,255,0.12)', borderTop: '1px solid var(--bt-glass-stroke)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div
                  style={{
                    width: 30,
                    height: 30,
                    background: 'var(--bt-ink)',
                    borderRadius: 9,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontStyle: 'italic',
                      color: 'var(--bt-ink-2)',
                      fontSize: 16,
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                  >
                    m
                  </span>
                </div>
                <span
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontStyle: 'italic',
                    fontSize: 20,
                    fontWeight: 500,
                    color: 'var(--bt-ink)',
                    letterSpacing: '-0.02em',
                  }}
                >
                  mamal
                </span>
              </div>
              <p className="text-[#6B6B6B] max-w-sm">
                {t('landing.footer.description')}
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-4">{t('landing.footer.legal')}</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/impressum" className="text-[#6B6B6B] hover:text-primary transition-colors">
                    {t('landing.footer.imprint')}
                  </Link>
                </li>
                <li>
                  <Link to="/datenschutz" className="text-[#6B6B6B] hover:text-primary transition-colors">
                    {t('landing.footer.privacy')}
                  </Link>
                </li>
                <li>
                  <Link to="/agb" className="text-[#6B6B6B] hover:text-primary transition-colors">
                    {t('landing.footer.terms')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact & Social */}
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-4">{t('landing.footer.contact')}</h4>
              <ul className="space-y-3">
                <li>
                  <a href="mailto:kontakt@mamal.de" className="text-[#6B6B6B] hover:text-primary transition-colors">
                    kontakt@mamal.de
                  </a>
                </li>
              </ul>
              <div className="flex items-center gap-4 mt-4">
                <a href="#" className="text-[#6B6B6B] hover:text-primary transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="text-[#6B6B6B] hover:text-primary transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-8 text-center">
            <p className="text-[#6B6B6B]">
              &copy; {new Date().getFullYear()} MAMAL. {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
